import { storeToRefs } from "pinia";
import { itemLetter, deepCopy, isEmpty } from "@utils/toolBox";
import genToken from "@utils/generateToken";
import { useConstantsStore } from "../constants";
import { useSurveyLocalesStore } from "./surveyLocales";

export const getCurrentLocale = () => {
  const { currentLocale } = storeToRefs(useSurveyLocalesStore());
  const { token } = currentLocale.value;
  return token;
};

const defaultCondition = {
  card_token: null,
  type: "is",
  values: [],
  operator: "or",
};

export const createItem = ({ length = 0, customTitle = "" }) => {
  const item = {
    button: itemLetter(length).toString(),
    conditions: [],
    title: {},
    token: genToken(),
  };
  if (!isEmpty(customTitle)) item.title[getCurrentLocale()] = customTitle;
  return item;
};
export const createMediaItem = () => ({ attachment: {}, token: genToken() });
export const createButtonItem = () => ({ type: "URL", url: {}, title: {}, token: genToken() });
export const createAnswerItem = ({ length = 0, customTitle = "", card }) => {
  const flags = card?.specs ?? {};
  const answer = {
    code: (length + 1).toString(),
    conditions: [],
    title: {},
    token: genToken(),
  };
  if (!isEmpty(customTitle)) answer.title[getCurrentLocale()] = customTitle;
  if (flags.is_other) answer.is_other = false;
  if (flags.screenout) answer.screenout = false;
  if (flags.exclusive_answers) answer.exclusive = false;
  if (flags.has_dynamic_answers) {
    answer.dynamic_include = false;
    answer.dynamic_exclude = false;
  }
  return answer;
};
// `image_url`, not `url`: `url` is a localized field on the API side.
export const createFocusImageItem = () => ({ token: genToken(), image_url: null, file_type: "file" });
export const createFocusGroupItem = ({ length = 0, customTitle = "" }) => {
  const fGroup = {
    code: (length + 1).toString(),
    title: {},
    desc: {},
    token: genToken(),
    images: [],
    focus_message: {},
    quick_reply: {},
    intro_order: ["text", "image", "quick_reply"],
    conditions: [],
    hidden: false,
    ai_condition_prompt: null,
  };
  if (!isEmpty(customTitle)) fGroup.title[getCurrentLocale()] = customTitle;
  return fGroup;
};
export const createCondition = () => ({ ...defaultCondition });
export const createConditionsGroup = () => ({
  token: genToken(),
  group_conditions: [{ ...defaultCondition }],
  group_operator: "or",
});
export const createRedirectItem = () => ({
  next_card_token: undefined,
  conditions: [],
  token: genToken(),
});
export const createAttributeItem = (length = 0) => ({
  code: (length + 1).toString(),
  title: {},
  conditions: [{ ...defaultCondition }],
  token: genToken(),
});
export const createRewardAmountItem = () => ({
  amount_cents: null,
  currency: "EUR",
  conditions: [],
  token: genToken(),
});
export const createQuickReplyItem = () => ({ next_card_token: "next_card", title: {}, token: genToken() });
export const createPopulationItem = (length = 0) => ({
  code: (length + 1).toString(),
  title: {},
  size: "50",
  description: {},
  token: genToken(),
});
export const createCellItem = (length = 0) => ({
  code: (length + 1).toString(),
  title: {},
  size: "50",
  token: genToken(),
});
export const createRangeItem = (type = "open") => {
  if (type === "min_max") return { min: "", max: "", value: "", token: genToken() };
  if (type === "open") return { from: "", to: "", token: genToken() };
  if (["closed", "contact"].includes(type)) return { from: [], to: "", token: genToken() };
  if (type === "conditions") return { conditions: [{ ...defaultCondition }], to: "", token: genToken() };
  if (type === "ai_chat_recode") return { from: "ai_chat_recode", to: "", token: genToken() };
  if (type === "open_ai_recode") return { from: "open_ai_recode", to: "", token: genToken() };
};
export const createImageItem = ({ length = 0, customType = "url" }) => {
  const image = {
    code: (length + 1).toString(),
    file_type: customType,
    url: {},
    title: {},
    attachment: {},
    conditions: [],
    token: genToken(),
  };
  image.title[getCurrentLocale()] = `Image ${length + 1}`;
  return image;
};
export function computeCodes(list) {
  return list.forEach((e, i) => (e.code = (i + 1).toString()));
}

export function updateCardPayload({ card, type = "m_text" }) {
  const specs = card.specs ?? {};
  const updatedCardAnswers = specs.answers?.map((a, i) => ({
    code: (i + 1).toString(),
    title: a.title,
    token: a.token || genToken(),
  }));

  // don't copy everything over, in case some fields aren't relevant to the "converted" card
  const updatedSpecs = {};
  if (specs.title) updatedSpecs.title = specs.title;
  if (specs.question) updatedSpecs.question = specs.question;
  if (specs.text && ["m_start", "m_text", "m_thanks"].includes(card.type)) updatedSpecs.text = specs.text;
  if (type === "m_start") {
    updatedSpecs.add_qr = true;
    updatedSpecs.quick_replies = specs.quick_replies?.length ? specs.quick_replies : [createQuickReplyItem()];
    updatedSpecs.consent_mode = specs.consent_mode ?? "default";
    updatedSpecs.emoji = specs.emoji ?? "👋";
    if (specs.consent) updatedSpecs.consent = specs.consent;
  }
  if (specs.answers && type.match(/^(q_(single|multiple|ranking|hidden|image_choice(_multiple)?|matrix|city|language)|ai_chat_v2)$/))
    updatedSpecs.answers = updatedCardAnswers;
  if (specs.conditions) updatedSpecs.conditions = specs.conditions;
  if (type.match(/o_loop/)) {
    updatedSpecs.position = specs.position ?? "start";
    updatedSpecs.loop_type = specs.loop_type ?? "answers";
  }

  return { ...card, type, specs: updatedSpecs };
}

export function duplicateCardPayload(card) {
  const newCard = { ...deepCopy(card), id: undefined, token: genToken(), name: "" };
  const specs = newCard.specs ?? (newCard.specs = {});

  if (card.type === "o_user_attributes") {
    specs.attribute_name = "";
    specs.attribute_values = [];
    specs.conditions = [];
  } else {
    if (specs.answers) specs.answers = specs.answers.map((a) => ({ ...a, token: genToken() }));
    if (specs.items) specs.items = specs.items.map((item) => ({ ...item, token: genToken() }));
    if (specs.focus_groups) specs.focus_groups = specs.focus_groups.map((fg) => ({ ...fg, token: genToken() }));
  }

  return newCard;
}

export function addCardPayload(type) {
  const { cardSubTypesInfos } = storeToRefs(useConstantsStore());
  const specs = deepCopy(cardSubTypesInfos.value[type]?.creation_defaults ?? {});
  var cardPayload = { token: genToken(), type: type, specs };
  if (type === "m_start") {
    specs.quick_replies = [createQuickReplyItem()];
  } else if (type.match(/o_loop/)) {
    cardPayload = [
      { token: genToken(), type: type, specs: { position: "start", loop_type: "answers" } },
      { token: genToken(), type: type, specs: { position: "end" } },
    ];
  } else if (type.match(/o_redirect/)) {
    specs.redirects = [createRedirectItem()];
  } else if (type.match(/o_user_attributes/)) {
    specs.attribute_values = [createAttributeItem()];
  } else if (type === "o_reward") {
    specs.amounts = [createRewardAmountItem()];
  } else if (type.match(/q_(single|multiple|ranking)/)) {
    specs.answers = [createAnswerItem({})];
  } else if (type.match(/q_hidden/)) {
    specs.answers = [createAnswerItem({})];
  } else if (type.match(/(matrix)/)) {
    specs.items = [createItem({})];
  } else if (type.match(/(o_ab)/)) {
    specs.populations = [createPopulationItem(0), createPopulationItem(1)];
  } else if (type === "o_cells") {
    specs.cells = [createCellItem(0), createCellItem(1)];
    specs.balance_on = [];
  } else if (type.match(/q_image_choice/)) {
    specs.answers = [createImageItem({ customType: "file" })];
  } else if (type.match(/m_gallery/)) {
    specs.images = [createMediaItem()];
  } else if (type.match(/(m_image|m_audio|m_video)/)) {
    specs.file_type = "file";
    specs.url = {};
    specs.attachment = {};
  } else if (type.match(/(ai_chat_v2)/)) {
    specs.answers = [createAnswerItem({})];
    specs.interview_guide = {};
    specs.focus_groups = [createFocusGroupItem({})];
    specs.response_format = "text";
    specs.numeric_limits = { min: null, max: null };
  }
  return Array.isArray(cardPayload) ? cardPayload : [cardPayload];
}
